import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { AsyncPipe, NgClass } from '@angular/common';
import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  model,
  OnDestroy,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { dsDtos, dsEnums } from '@vectronic/openApi-admin';
import { AlertComponent } from '@vectronic/ui';
import { photoFormats, SnackbarService, videoFormats } from '@vectronic/util';
import { of, Subscription } from 'rxjs';
import {
  downloadScheduleJsonFile,
  importScheduleJsonFromInputEvent,
} from '../../../helpers/schedule-json.helper';
import { CameraScheduleService } from '../../../services/camera-schedule.service';
import { RemoteCommandsService } from '../../../services/commands/remote-commands.service';
import { GpsXFileImportService } from '../../../services/gps-x-file-import.service';
import { RemoteCommandProviderService } from '../../../services/remote-command-provider.service';
import { ScheduleTemplatesProviderService } from '../../../services/schedule-templates-provider.service';
import { ScheduleTemplateSelectorComponent } from '../../schedule-template-selector/schedule-template-selector.component';
import { CameraCalendarComponent } from '../camera-calendar/camera-calendar.component';
import { CameraRuleComponent } from '../camera-rule/camera-rule.component';
import { CAMERA_SCHEDULE_STRINGS } from './camera-schedule.strings';

@Component({
  selector: 'rc-camera-schedule',
  templateUrl: './camera-schedule.component.html',
  styleUrls: ['./camera-schedule.component.scss'],
  imports: [
    NgClass,
    MatIconButton,
    MatIcon,
    MatTooltip,
    CameraRuleComponent,
    MatButton,
    CameraCalendarComponent,
    AsyncPipe,
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    ScheduleTemplateSelectorComponent,
    FormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    AlertComponent,
  ],
})
export class CameraScheduleComponent implements OnInit, OnDestroy, AfterContentChecked {
  readonly strings = CAMERA_SCHEDULE_STRINGS;

  cameraScheduleService = inject(CameraScheduleService);
  gpsXFileImportService = inject(GpsXFileImportService);
  remoteCommandProviderService = inject(RemoteCommandProviderService, { optional: true });
  remoteCommandsService = inject(RemoteCommandsService);
  private cdref = inject(ChangeDetectorRef);
  private templatesProvider = inject(ScheduleTemplatesProviderService, { optional: true });
  private snackbarService = inject(SnackbarService);

  selectedCollarIds = input<number[]>();
  videoFormatsOptions = input<{ value: number; label: string }[]>();
  photoFormatsOptions = input<{ value: number; label: string }[]>();
  showSubmit = input<boolean>();
  showReset = input<boolean>();
  isMoreThanOneCameraType = input<boolean>();
  hasSameCameraId = input<boolean>();
  cameraIds = input<number[]>();
  showRemoveSchedule = input(true);
  hideChangedSchedules = input<boolean>();
  isRulesDeletable = input<boolean>();
  hideCameraFormat = input<boolean>();
  showScheduleRemove = input<boolean>(true);
  readonly = input<boolean>();
  showOverrideWarning = input<boolean>();
  isDisabledByEmptySchedule = input<boolean>(false);
  ignoreShortHeaderMessage = input<boolean>(false);
  showLoadFromTemplates = input<boolean>(false);
  showSaveAsTemplate = input<boolean>(true);
  showImportJson = input<boolean>(true);
  showPreview = model<boolean>();
  warningMessages = input<string[]>();
  hideRules = input<boolean>(false);
  title = input<string>('Camera Schedule');
  hasHighActivityCameraTrigger = input<boolean>(false);

  scheduleSubmit = output<dsDtos.Schedule>();
  scheduleReset = output<dsDtos.Schedule['scheduleType']>();
  rulesIndexes$ = this.cameraScheduleService.rulesIndexes$;

  showLoadGpsXFile = computed(() => this.gpsXFileImportService.canLoad('CAMERA', this.readonly()));
  gpsXFileAccept = computed(
    () => this.gpsXFileImportService.getAcceptedFileExtension('CAMERA') ?? '',
  );

  cameraFormats = videoFormats;
  cameraRulesSubscription: Subscription;
  mode: dsEnums.CameraScheduleRuleType = 'VIDEO';
  cameraFormatFormControl = new FormControl('');

  private closeTooltipTimer: number | null = null;
  isOpen = signal(false);
  isLoadScheduleOpen = signal(false);
  loadScheduleTrigger = signal<CdkOverlayOrigin | undefined>(undefined);

  isSaveScheduleOpen = signal(false);
  saveScheduleTrigger = signal<CdkOverlayOrigin | undefined>(undefined);
  templateName = signal('');

  canExportJson = computed(
    () =>
      !!Object.values(this.cameraScheduleService.rules?.() ?? {}).filter(
        (rule) => rule && Object.keys(rule).length > 0,
      ).length,
  );

  private importJsonInput = viewChild.required<ElementRef<HTMLInputElement>>('importJsonInput');
  private gpsXFileInput = viewChild.required<ElementRef<HTMLInputElement>>('gpsXFileInput');

  remoteSchedules = toSignal(this.remoteCommandProviderService?.remoteSchedules$ ?? of());

  constructor() {
    effect(() => {
      this.remoteCommandsService.ignoreShortHeaderMessage$.next(this.ignoreShortHeaderMessage());
    });
  }

  ngOnInit() {
    this.cameraRulesSubscription = this.cameraScheduleService.rules$.subscribe((rules) => {
      const rule = rules[0];
      this.updateMode(rule);
    });
    this.cameraScheduleService.hideCameraFormat.set(this.hideCameraFormat());
  }

  ngAfterContentChecked() {
    this.cdref.detectChanges();
  }

  updateMode(rule: dsDtos.CameraScheduleRule) {
    if (rule?.cameraScheduleRuleType === 'VIDEO') {
      this.cameraFormats = videoFormats;
      this.mode = 'VIDEO';
    } else {
      this.cameraFormats = photoFormats;
      this.mode = 'PICTURE';
    }
  }

  onClickAddRule() {
    this.cameraScheduleService.onClickAddRule();
  }

  onRemoveRule(index: number) {
    this.cameraScheduleService.onRemoveRule(index, this.readonly());
  }

  isDisabled() {
    if (
      this.remoteSchedules()?.CAMERA &&
      this.remoteSchedules().CAMERA?.length !== this.selectedCollarIds()?.length &&
      this.selectedCollarIds()?.length
    ) {
      return true;
    }
    if (this.isMoreThanOneCameraType()) {
      return true;
    }
    if (this.isDisabledByEmptySchedule()) {
      return true;
    }
    return this.isMaxSchedulesReachedAndIsNotFilled();
  }

  isMaxSchedulesReachedAndIsNotFilled() {
    return (
      this.remoteCommandsService.isMaxSchedulesReached() &&
      !this.cameraScheduleService.isFilledAndChanged()
    );
  }

  collarsWithShortHeaderFormatJoin() {
    return this.remoteCommandsService.collarsWithNoShortHeaderFormat()?.join(', ');
  }

  onClickRemove() {
    this.cameraScheduleService.onRemoveSchedule();
    this.cameraScheduleService.setToDefaultSchedule();
  }

  ngOnDestroy() {
    this.cameraRulesSubscription?.unsubscribe();
  }

  onSaveMenuClick(trigger: CdkOverlayOrigin) {
    this.saveScheduleTrigger.set(trigger);
  }

  onOpenSaveTemplate() {
    this.isSaveScheduleOpen.set(true);
  }

  onConfirmSaveTemplate() {
    if (!this.templateName()) return;
    const template: dsDtos.AddScheduleTemplateCustomer = {
      schedule: this.cameraScheduleService.toSchedule(),
      templateName: this.templateName(),
    };
    const templateName = this.templateName();
    this.templatesProvider?.add(template).subscribe(() => {
      this.isSaveScheduleOpen.set(false);
      this.templateName.set('');
      this.snackbarService.success(`Template "${templateName}" saved successfully`);
    });
  }

  onExportJson() {
    downloadScheduleJsonFile(this.cameraScheduleService.toSchedule());
  }

  onSubmit() {
    this.scheduleSubmit.emit({
      scheduleType: 'CAMERA',
      rules: Object.values(this.cameraScheduleService.rules$.value),
      userDefined: true,
    });
  }

  onReset() {
    this.scheduleReset.emit('CAMERA');
  }

  onClickRemoveCamera() {
    this.cameraScheduleService.setEmptySchedule();
  }
  onAddSchedule() {
    this.cameraScheduleService.onAddSchedule();
  }

  onLoadMenuClick(trigger: CdkOverlayOrigin) {
    this.loadScheduleTrigger.set(trigger);
  }

  onOpenTemplateSelector() {
    this.isLoadScheduleOpen.set(true);
  }

  onImportJson() {
    this.importJsonInput().nativeElement.click();
  }

  onLoadGpsXFile() {
    this.gpsXFileInput().nativeElement.click();
  }

  async onLoadGpsXFileSelected(event: Event) {
    await this.gpsXFileImportService.importScheduleFromEvent(event, 'CAMERA');
  }

  async onImportJsonSelected(event: Event) {
    const { schedule, errorMessage } = await importScheduleJsonFromInputEvent(event, 'CAMERA');

    if (errorMessage) {
      this.snackbarService.error(errorMessage);
      return;
    }

    if (!schedule) {
      return;
    }

    if (!schedule.cameraScheduleRules?.length) {
      this.snackbarService.error('Imported schedule does not contain any camera rules.');
      return;
    }

    this.cameraScheduleService.loadRules(schedule.cameraScheduleRules);
    this.snackbarService.success('Imported CAMERA schedule from JSON.');
  }

  onTemplateSelected(template: dsDtos.ScheduleTemplateAdmin) {
    if (template.schedule?.cameraScheduleRules) {
      const rules = template.schedule.cameraScheduleRules.map((rule) => {
        const validFormats =
          rule.cameraScheduleRuleType === 'VIDEO'
            ? this.videoFormatsOptions()
            : this.photoFormatsOptions();
        const isFormatValid =
          !rule.cameraFormatId ||
          !validFormats?.length ||
          validFormats.some((f) => f.value === rule.cameraFormatId);
        if (!isFormatValid) {
          this.snackbarService.info(
            'One or more camera formats from the template are not supported by this collar. Please select a valid format.',
          );
          return { ...rule, cameraFormatId: null };
        }
        return rule;
      });
      this.cameraScheduleService.loadRules(rules as dsDtos.CameraScheduleRule[]);
    }
    this.isLoadScheduleOpen.set(false);
  }

  onTooltipMouseEnter() {
    if (this.closeTooltipTimer) {
      window.clearTimeout(this.closeTooltipTimer);
      this.closeTooltipTimer = null;
    }
    this.isOpen.set(true);
  }

  onTooltipMouseLeave() {
    this.closeTooltipTimer = window.setTimeout(() => {
      this.isOpen.set(false);
      this.closeTooltipTimer = null;
    }, 300);
  }

  onClickConsult($event: MouseEvent) {
    $event.preventDefault();
    const url = 'https://www.vectronic-aerospace.com/editing-schedules/';
    const { desktop } = window;
    if (desktop) desktop.app.openUrlInDefaultBrowserTab(url);
    else window.open(url, '_blank', 'noopener');
  }

  onClickContact($event: MouseEvent) {
    $event.preventDefault();
    const url = 'https://www.vectronic-aerospace.com/support/';
    const { desktop } = window;
    if (desktop) desktop.app.openUrlInDefaultBrowserTab(url);
    else window.open(url, '_blank', 'noopener');
  }
}
